if (!customElements.get('ht-metal-ticker')) {
  const SYMBOLS = { gold: 'XAU', silver: 'XAG', platinum: 'XPT', palladium: 'XPD' };
  const REFRESH_MS = 60000;

  const fmt = (n) =>
    '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  class HtMetalTicker extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.workerUrl = this.dataset.workerUrl;
      this.speed = Number(this.dataset.speed) || 60;
      this.keys = [...new Set([...this.track.children].map((el) => el.dataset.key))].filter((k) => SYMBOLS[k]);
      // "Pills" layout: static, centred chips on wide screens; narrow screens keep the marquee.
      this.pills = this.dataset.layout === 'pills' && window.matchMedia('(min-width: 750px)').matches;
      this.classList.toggle('is-pills', this.pills);

      if (!this.pills) this.#buildMarquee();
      this.#refresh();
      this.timer = setInterval(() => this.#refresh(), REFRESH_MS);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
      cancelAnimationFrame(this.frame);
    }

    // Infinite marquee: one set of items plus enough clones to always fill the screen.
    #buildMarquee() {
      this.set = document.createElement('span');
      this.set.className = 'ht-ticker__set';
      [...this.track.children].forEach((el) => this.set.appendChild(el));
      this.track.appendChild(this.set);

      requestAnimationFrame(() => {
        this.#measure();
        const screen = window.innerWidth || document.documentElement.clientWidth;
        const copies = Math.ceil((screen * 2) / Math.max(this.setWidth, 200)) + 2;
        for (let i = 0; i < copies; i++) {
          const clone = this.set.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => el.removeAttribute('data-shopify-editor-block'));
          this.track.appendChild(clone);
        }

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced) return;

        let pos = 0;
        let paused = false;
        let last = null;
        this.addEventListener('mouseenter', () => (paused = true));
        this.addEventListener('mouseleave', () => (paused = false));

        const step = (ts) => {
          if (last !== null && !paused && this.setWidth > 0) {
            pos += (this.speed * (ts - last)) / 1000;
            if (pos >= this.setWidth) pos -= this.setWidth;
            this.track.style.transform = `translateX(-${pos.toFixed(2)}px)`;
          }
          last = ts;
          this.frame = requestAnimationFrame(step);
        };
        this.frame = requestAnimationFrame(step);
      });
    }

    #measure() {
      if (!this.set) return;
      this.setWidth = this.set.offsetWidth || this.setWidth || 0;
    }

    // change === null -> only update the price, keep the current arrow
    #apply(key, price, change) {
      this.querySelectorAll(`.ht-ticker__item[data-key="${key}"]`).forEach((item) => {
        item.querySelector('.ht-ticker__price').textContent = fmt(price);
        if (change === null || change === undefined) return;
        const el = item.querySelector('.ht-ticker__change');
        if (this.dataset.layout === 'pills') {
          const sign = change > 0 ? '+' : change < 0 ? '-' : '';
          el.className = 'ht-ticker__change' + (change > 0 ? ' up' : change < 0 ? ' down' : '');
          el.textContent = sign + Math.abs(change).toFixed(2);
          return;
        }
        if (change > 0) {
          el.className = 'ht-ticker__change up';
          el.innerHTML = '<span class="arrow">▲</span>' + fmt(change);
        } else if (change < 0) {
          el.className = 'ht-ticker__change down';
          el.innerHTML = '<span class="arrow">▼</span>' + fmt(Math.abs(change));
        } else {
          el.className = 'ht-ticker__change';
          el.textContent = fmt(0);
        }
      });
    }

    // Backup when the Worker is down: price straight from gold-api.com, no arrow update.
    #fallback() {
      return Promise.all(
        this.keys.map((k) =>
          fetch('https://api.gold-api.com/price/' + SYMBOLS[k])
            .then((r) => {
              if (!r.ok) throw new Error('HTTP ' + r.status);
              return r.json();
            })
            .then((j) => {
              if (typeof j.price === 'number' && j.price > 0) this.#apply(k, j.price, null);
            })
            .catch((e) => console.warn(`[metals-ticker] fallback ${k}:`, e.message))
        )
      );
    }

    #refresh() {
      const request = this.workerUrl
        ? fetch(this.workerUrl).then((r) => {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
          })
        : Promise.reject(new Error('no worker URL'));

      request
        .then((json) => {
          this.keys.forEach((k) => {
            const d = json.data && json.data[k];
            if (d && d.price > 0) this.#apply(k, d.price, d.change);
          });
        })
        .catch((e) => {
          console.warn('[metals-ticker] worker:', e.message);
          return this.#fallback();
        })
        .then(() => requestAnimationFrame(() => this.#measure()));
    }
  }

  customElements.define('ht-metal-ticker', HtMetalTicker);
}
